<script setup lang="ts">
import ShimmerLoader from '@/components/common/animations/ShimmerLoader.vue'
import { truncateString } from '@/components/helper'
import { app } from '@/composition/app'
import { GroupState } from '@/composition/groups'
import { UserState } from '@/composition/users'
import { DEFAULT_GROUP_LOGO } from '@/helper/const'
import { replaceBucketUrlWithCdnUrl } from '@/helper/ImageUpload.helper'
import { router } from '@/router'
import {
  ChevronSelectorVerticalIcon,
  Pin02Icon,
} from '@gohighlevel/ghl-icons/24/outline'
import { UIAvatar, UITooltip } from '@gohighlevel/ghl-ui'
import { vOnClickOutside } from '@vueuse/components'
import { computed, h, nextTick, onMounted, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'

const isUserLoggedIn = computed(() => UserState.state?.currentUser?.fullName)

const { t } = useI18n()

const isOpen = ref(false)
const pagesize = 30
const isGroupListLoading = ref(false)

const groupList = computed<
  {
    id: string
    key: string
    label: string
    icon: any
    imageUrl: string
  }[]
>(() => {
  return GroupState.state.groupList.map((group: any) => {
    const groupOption = {
      id: group._id.toString(),
      key: group.slug,
      label: truncateString(group.name, 30),
      imageUrl:
        replaceBucketUrlWithCdnUrl(group?.branding?.logo) || DEFAULT_GROUP_LOGO,
      icon: () => {
        const groupAvatar = h(UIAvatar, {
          style: {
            margin: '8px',
          },
          size: 24,
          class: 'font-medium text-communities-background  text-md',
          src:
            replaceBucketUrlWithCdnUrl(group?.branding?.logo) ||
            DEFAULT_GROUP_LOGO,
        })
        return groupAvatar
      },
    }
    return groupOption
  })
})
const toggleDropdown = () => {
  isOpen.value = !isOpen.value
}
const closeDropdown = () => {
  isOpen.value = false
}
function navigateToGroup(groupSlug) {
  if (GroupState.state.groupData?.slug === groupSlug) {
    closeDropdown()
    return
  }
  router.push({ name: 'home', params: { groupSlug: groupSlug } })
  // redirectToGroup(groupSlug)
}
async function registerObserver2() {
  nextTick(async () => {
    const observer = new IntersectionObserver(
      async entries => {
        if (entries[0].isIntersecting && !isGroupListLoading.value) {
          isGroupListLoading.value = true
          await GroupState.getGroupByLocation({ limit: pagesize })
          await GroupState.computeUnpinnedGroups()
          isGroupListLoading.value = false
        }
      },
      {
        rootMargin: '100px', // Increased margin to trigger earlier
        threshold: 0.5,
      }
    )
    const target = document.getElementById('observe-list-end')
    if (target) {
      observer.observe(target)
    }
  })
}

async function pinGroup(groupId: string) {
  await GroupState.pinGroup(groupId)
}
async function unpinGroup(groupId: string) {
  await GroupState.unpinGroup(groupId)
}
watch(isOpen, () => {
  if (isOpen.value) {
    registerObserver2()
  }
})
watch(groupList, async () => {
  await GroupState.computeUnpinnedGroups()
})

onMounted(async () => {
  await GroupState.fetchPinnedGroups()
})
</script>
<template>
  <div
    v-on-click-outside="closeDropdown"
    class="relative"
    :class="app.state.isMobile ? 'pl-1' : 'pl-2'"
  >
    <UITooltip
      :placement="'bottom'"
      :trigger="isOpen ? 'manual' : 'hover'"
      :style="{ border: '1px solid var(--border)' }"
      :disabled="isOpen"
    >
      <template #trigger>
        <button
          class="flex cursor-pointer flex-col items-center justify-center rounded-full border-0 bg-communities-sidebar-background text-communities-sidebar-font-secondary hover:bg-communities-sidebar-fill"
          :class="app.state.isMobile ? 'h-7 w-7' : 'h-10 w-10'"
          @click="toggleDropdown"
        >
          <ChevronSelectorVerticalIcon
            :class="app.state.isMobile ? 'h-4 w-4' : 'h-6 w-6'"
          />
        </button>
      </template>
      <div
        class="font-roboto flex h-[36px] w-[150px] flex-col items-center justify-center text-center text-[13px] font-semibold leading-[20px]"
      >
        {{ t('groupInfo.switchToOtherGroups') }}
      </div>
    </UITooltip>
    <div
      v-if="isOpen"
      class="group-switcher-dropdown absolute left-1/2 top-full mt-1 max-h-[40vh] w-[260px] -translate-x-1/2 transform overflow-y-auto rounded-lg border border-solid border-communities-border text-communities-sidebar-font-primary no-scrollbar"
    >
      <div v-if="groupList.length > 0">
        <!-- PINNED GROUP LIST -->
        <button
          v-for="group in GroupState.state.pinnedGroupList"
          :key="group.id"
          class="dropdown-item relative h-[45px] w-full border border-b-0 border-t-0 border-solid border-communities-border bg-communities-sidebar-background text-communities-sidebar-font-primary hover:bg-communities-sidebar-fill"
          @click="navigateToGroup(group.slug)"
        >
          <img
            :src="group?.logo || DEFAULT_GROUP_LOGO"
            class="mr-3 h-[18px] w-[18px] rounded-[2px]"
            alt="Group Logo"
          />
          <span
            class="font-roboto h-[20px] truncate text-[13px] font-normal leading-[20px] text-communities-sidebar-font-primary"
            >{{ group.name }}</span
          >
          <UITooltip
            :placement="'right-start'"
            trigger="hover"
            :style="{ border: '1px solid var(--border)' }"
          >
            <template #trigger>
              <div
                class="group-hover:bg-communities-sidebar-selected-item-fill absolute right-[8px] flex h-[16px] w-[16px] items-center justify-center rounded-full"
                @click.stop="unpinGroup(group.id)"
              >
                <!-- <img :src="PinnedIconDarkFill" class="h-[12px] w-[12px]" /> -->
                <Pin02Icon
                  class="h-[12px] w-[12px] fill-communities-sidebar-font-primary"
                  alt="Pin Group Icon"
                />
              </div>
            </template>
            <div
              class="font-roboto flex h-[36px] max-w-[150px] flex-col items-center justify-center text-center text-[13px] font-semibold leading-[20px]"
            >
              {{ t('groupInfo.unpinGroup') }}
            </div>
          </UITooltip>
        </button>

        <!-- UNPINNED GROUP LIST -->
        <button
          v-for="group in GroupState.state.unpinnedGroupList"
          :key="group.id"
          class="dropdown-item group relative h-[45px] w-full border border-b-0 border-t-0 border-solid border-communities-border bg-communities-sidebar-background text-communities-sidebar-font-primary hover:bg-communities-sidebar-fill"
          @click="navigateToGroup(group.slug)"
        >
          <img
            :src="group?.logo || DEFAULT_GROUP_LOGO"
            class="mr-3 h-[18px] w-[18px] rounded-[2px]"
            alt="Group Logo"
          />
          <span
            class="font-roboto h-[20px] truncate text-[13px] font-normal leading-[20px] text-communities-sidebar-font-primary"
            >{{ group.name }}</span
          >
          <UITooltip
            v-if="isUserLoggedIn"
            :placement="'right-start'"
            trigger="hover"
          >
            <template #trigger>
              <div
                class="hover:bg-communities-sidebar-selected-item-fill absolute right-[8px] hidden h-[16px] w-[16px] items-center justify-center rounded-full group-hover:flex"
                @click.stop="
                  GroupState.state.pinnedGroupList.length < 4 &&
                    pinGroup(group.id)
                "
              >
                <Pin02Icon
                  class="communities-sidebar-font-primary h-[12px] w-[12px]"
                  alt="Pin Group Icon"
                />
              </div>
            </template>
            <div
              class="font-roboto flex h-[36px] max-w-[150px] flex-col items-center justify-center text-center text-[13px] font-semibold leading-[20px]"
            >
              <div v-if="GroupState.state.pinnedGroupList.length < 4">
                {{ t('groupInfo.pinGroup') }}
              </div>
              <div v-else>{{ t('groupInfo.maxPinnedGroupsLimit') }}</div>
            </div>
          </UITooltip>
        </button>

        <div id="observe-list-end" class="h-[2px] w-full">
          <ShimmerLoader
            v-if="isGroupListLoading"
            :repeat="1"
            :is-loading="true"
            class-name="h-[45px] w-[100%] group-switcher-shimmer"
          />
        </div>
      </div>
    </div>
  </div>
</template>
<style>
.group-switcher-dropdown {
  max-height: 40vh;
  overflow-y: auto;
}
.dropdown-item {
  display: flex;
  align-items: center;
  padding: 10px;
  cursor: pointer;
  overflow: hidden;
}
</style>
<style>
.shimmer.group-switcher-shimmer {
  background: var(--sidebar-background);
  background-image: linear-gradient(
    to right,
    var(--sidebar-background) 0%,
    var(--sidebar-background-gradient) 20%,
    var(--sidebar-background) 40%,
    var(--sidebar-background) 100%
  );
}
</style>
